import type { GameObj, Vec2 } from "kaplay";
import type { GameConfig } from "../config";
import { loadScores, recentEntryKey, syncFromServer, type HighScore } from "../highscore";
import { initAudio, isMuted, toggleMute, startMenuMusic, stopMenuMusic } from "../audio";
import { NAME_INPUT_ID } from "./game";
import { view } from "../viewport";

// Speaker icon (toggles to a crossed-out speaker when muted).
function drawMuteIcon(cx: number, cy: number, muted: boolean) {
  const col = muted ? rgb(120, 120, 120) : rgb(255, 233, 168);
  drawRect({ pos: vec2(cx - 11, cy - 3), width: 4, height: 6, color: col }); // base
  drawPolygon({ pts: [vec2(cx - 7, cy - 3), vec2(cx - 1, cy - 8), vec2(cx - 1, cy + 8), vec2(cx - 7, cy + 3)], color: col }); // cone
  if (muted) {
    drawLine({ p1: vec2(cx + 3, cy - 7), p2: vec2(cx + 12, cy + 7), width: 2, color: rgb(210, 60, 60) });
    drawLine({ p1: vec2(cx + 12, cy - 7), p2: vec2(cx + 3, cy + 7), width: 2, color: rgb(210, 60, 60) });
  } else {
    drawLine({ p1: vec2(cx + 3, cy - 5), p2: vec2(cx + 6, cy), width: 2, color: col });
    drawLine({ p1: vec2(cx + 6, cy), p2: vec2(cx + 3, cy + 5), width: 2, color: col });
    drawLine({ p1: vec2(cx + 8, cy - 8), p2: vec2(cx + 12, cy), width: 2, color: col });
    drawLine({ p1: vec2(cx + 12, cy), p2: vec2(cx + 8, cy + 8), width: 2, color: col });
  }
}

// "?" help button.
function drawHelpIcon(cx: number, cy: number) {
  drawCircle({ pos: vec2(cx, cy), radius: 13, color: rgb(36, 24, 52), outline: { width: 2, color: rgb(255, 233, 168) } });
  drawText({ text: "?", size: 16, font: "press2p", pos: vec2(cx, cy - 1), anchor: "center", color: rgb(255, 233, 168) });
}

// Full-screen instructions overlay: purpose + desktop/mobile controls.
function drawHelpOverlay() {
  const W = width();
  drawRect({ pos: vec2(0, 0), width: W, height: height(), color: rgb(8, 6, 16) });
  const cx = W / 2;
  drawText({ text: "HOW TO PLAY", size: 18, font: "press2p", pos: vec2(cx, 34), anchor: "center", color: rgb(255, 48, 48) });
  drawText({
    text: "Blast zombies in the windows before they climb out and reach you. Only a HEADSHOT stops a walker. Survive for a high score.",
    size: 12,
    font: "press2p",
    pos: vec2(cx, 76),
    anchor: "center",
    align: "center",
    width: 540,
    color: rgb(255, 233, 168),
  });

  const rows: Array<[string, number, [number, number, number]]> = [
    ["DESKTOP", 150, [127, 232, 90]],
    ["MOUSE aim  -  LEFT fire", 174, [255, 233, 168]],
    ["RIGHT reload  -  M mute", 194, [255, 233, 168]],
    ["MOBILE", 232, [127, 232, 90]],
    ["TAP to aim + fire", 256, [255, 233, 168]],
    ["TWO-FINGER TAP to reload", 276, [255, 233, 168]],
    ["speaker icon to mute", 296, [255, 233, 168]],
    ["BONUS", 334, [127, 232, 90]],
    ["Shoot the BAT for x2 score", 358, [255, 233, 168]],
    ["Survive for bonus points", 378, [255, 233, 168]],
  ];
  for (const [t, y, c] of rows) {
    const isHeader = y === 150 || y === 232 || y === 334;
    drawText({ text: t, size: isHeader ? 12 : 10, font: "press2p", pos: vec2(cx, y), anchor: "center", color: rgb(...c) });
  }
  drawText({ text: "TAP / CLICK TO CLOSE", size: 10, font: "press2p", pos: vec2(cx, 422), anchor: "center", color: rgb(150, 150, 150) });
}

// Title scene: animated title, the top-N high-score table, and a start prompt.
// Coordinates are in the 640x448 buffer (2x the brief's 320x224 grid).
export function createMenuScene(config: GameConfig) {
  scene("menu", () => {
    setCursor("default"); // restore the system cursor on the title screen
    document.getElementById(NAME_INPUT_ID)?.remove(); // clear any leftover name input
    startMenuMusic(); // resumes the title theme (audible once audio is unlocked, e.g. returning from a game)

    // Title: both lines rise up out of a clip line when the menu loads.
    const SIZE = 28;
    const RISE = 0.75; // seconds per line (50% slower)
    const STAGGER = 0.27; // second line trails the first
    const titleLines = [
      { txt: "HOUSE OF THE", y: 110 },
      { txt: "RISING DEATH", y: 146 },
    ];
    let titleT = 0;
    const title = add([z(10)]);
    title.onUpdate(() => (titleT += dt()));
    title.onDraw(() => {
      titleLines.forEach((ln, i) => {
        const p = Math.min(1, Math.max(0, (titleT - i * STAGGER) / RISE));
        const e = 1 - Math.pow(1 - p, 3); // ease-out
        const yNow = ln.y + (1 - e) * (SIZE + 10); // start one line below, rise into place
        const clipY = ln.y + SIZE / 2 + 2; // hide anything below the line's baseline
        drawMasked(
          () =>
            drawText({ text: ln.txt, size: SIZE, font: "press2p", pos: vec2(center().x, yNow), anchor: "center", color: rgb(255, 48, 48) }),
          () => drawRect({ pos: vec2(0, -50), width: width(), height: clipY + 50, color: rgb(255, 255, 255) }),
        );
      });
    });

    // The middle panel alternates between the HIGH SCORES table and a CREDITS screen.
    let showingCredits = false;

    // Scores: render from the local cache immediately, then reconcile with the shared
    // server table and re-render when it arrives.
    const scoresHeader = add([text("HIGH SCORES", { size: 14, font: "press2p" }), pos(center().x, 198), anchor("center"), color(255, 48, 48)]);
    let scoreRows: GameObj[] = [];
    const renderScores = (scores: HighScore[]) => {
      scoreRows.forEach((o) => destroy(o));
      scoreRows = scores.map((s, i) =>
        add([
          text(`${i + 1}  ${s.name.padEnd(6)}  ${String(s.score).padStart(6)}`, { size: 14, font: "press2p" }),
          pos(center().x, 226 + i * 24),
          anchor("center"),
          color(recentEntryKey(s) ? rgb(127, 232, 90) : rgb(255, 233, 168)), // highlight new entry
        ]),
      );
      applyPanel();
    };

    // Credits.
    const creditRows: Array<[string, number, number, [number, number, number]]> = [
      ["Made by Jirka & Claude", 240, 14, [255, 233, 168]],
      ["(C) 2026", 282, 14, [255, 48, 48]],
      ["Vyrobeno v Brně", 320, 12, [127, 232, 90]],
    ];
    const creditObjs = creditRows.map(([t, y, sz, c]) =>
      add([text(t, { size: sz, font: "press2p" }), pos(center().x, y), anchor("center"), color(rgb(...c))]),
    );

    function applyPanel() {
      scoresHeader.hidden = showingCredits;
      scoreRows.forEach((o) => (o.hidden = showingCredits));
      creditObjs.forEach((o) => (o.hidden = !showingCredits));
    }

    renderScores(loadScores(config.highscore.table_size));
    syncFromServer(config.highscore.table_size).then((s) => {
      if (s) renderScores(s);
    });
    applyPanel();
    loop(6, () => {
      showingCredits = !showingCredits;
      applyPanel();
    });

    const prompt = add([
      text("CLICK / PRESS ENTER TO START", { size: 14 }),
      pos(center().x, 404),
      anchor("center"),
      color(255, 233, 168), // HUD amber
      opacity(1),
    ]);

    // Classic arcade blink: hard on/off toggle (no fade).
    loop(0.5, () => {
      prompt.opacity = prompt.opacity > 0 ? 0 : 1;
    });

    // Corner buttons: mute (top-right), help (top-left) — anchored to the visible
    // region so they stay reachable under the mobile side-crop.
    const muteX = () => view.right - 26;
    const helpX = () => view.left + 26;
    const btnY = () => view.top + 24;
    const overBtn = (p: Vec2, bx: number, by: number) => Math.abs(p.x - bx) <= 18 && Math.abs(p.y - by) <= 18;
    add([z(20)]).onDraw(() => drawMuteIcon(muteX(), btnY(), isMuted()));
    add([z(20)]).onDraw(() => drawHelpIcon(helpX(), btnY()));

    // Instructions overlay.
    let showingHelp = /help/.test(location.hash); // debug: open via #help
    add([z(50)]).onDraw(() => {
      if (showingHelp) drawHelpOverlay();
    });

    // Tap/click: close help if open, else toggle mute / open help / start.
    const interact = (p: Vec2) => {
      initAudio(); // first user gesture: resume the audio context
      startMenuMusic(); // and kick off the title theme now that audio is unlocked
      if (showingHelp) {
        showingHelp = false;
        return;
      }
      if (overBtn(p, muteX(), btnY())) {
        toggleMute();
        return;
      }
      if (overBtn(p, helpX(), btnY())) {
        showingHelp = true;
        return;
      }
      stopMenuMusic();
      go("game");
    };
    onMousePress("left", () => interact(mousePos()));
    onTouchEnd((p) => interact(p));
    onKeyPress("enter", () => {
      initAudio();
      if (showingHelp) {
        showingHelp = false;
        startMenuMusic();
      } else {
        stopMenuMusic();
        go("game");
      }
    });
  });
}
